import axios from "axios";
import { saveAs } from "file-saver";
import cookie from "js-cookie";
import { requestLogin } from "../constants/constant";
import { REACT_APP_AXIOS_TIMEOUT } from "../constants/stringConstants";
import { containsMaliciousCode } from "../utils/sanitizePayload";
/**
 * Creates axios instance
 * @link https://github.com/axios/axios
 */
let instanceSecure;
let instanceSecureForMultipart;
export const getToken = () => {
  return new Promise((resolve, reject) => {
    const token = cookie.get("dotgo-token");

    if (!token) {
      reject("No token found");
    }
    const AUTH_TOKEN = JSON.parse(token)[0].access_token;
    resolve(AUTH_TOKEN);
  });
};

if (!instanceSecure) {
  instanceSecure = axios.create({
    timeout: REACT_APP_AXIOS_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
    },
  });
}

if (!instanceSecureForMultipart) {
  instanceSecureForMultipart = axios.create({
    timeout: REACT_APP_AXIOS_TIMEOUT,
    headers: {
      "Content-Type": "multipart/form-data",
      "Access-Control-Allow-Origin": "*",
    },
  });
}

/**
 * Request Interceptor:
 * It intercept the request,
 * used for making some change in request before
 * it reaches to server.
 */
instanceSecure.interceptors.request.use(
  function (config) {
    if (config.data !== null && typeof config.data === "object" && containsMaliciousCode(config.data)) {
      window.location.replace("/error?ref=redirect");
      return
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor:
 * It intercept the response,
 * used for making some change in response before
 * it reaches to component who requested it
 */
instanceSecure.interceptors.response.use(
  function (response) {
    if (response?.status === 403 || response?.status === 404) {
      if (process.env.REACT_APP_ENVIRONMENT !== "local") {
        window.location.replace("/error?ref=redirect");
      }
      return;
    }
    return response;
  },
  function (error) {
    const statusCode = error?.response?.status;
    if ((statusCode >= 500 && statusCode <= 599) || statusCode === 403) {
      if (process.env.REACT_APP_ENVIRONMENT === "local") {
        return Promise.reject(error);
      } else {
        window.location.replace("/error?ref=redirect");
      }
      return;
    }
    return Promise.reject(error);
  }
);

export default class SecureService {
  /**
   * Get Request
   * @param {url} url url for request
   */
  static getCall(url) {
    return getToken()
      .then((token) => {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        return new Promise(function (resolve, reject) {
          instanceSecure
            .get(url, config)
            .then(function (response) {
              resolve(response);
            })
            .catch(function (error) {
              reject(error);
            });
        });
      })
      .catch((e) => {
        try {
          console.error("[API CALL] Error:[GET]", e);
          if (e?.response?.status === 401) {
            console.error("[API CALL] Error while connecting server:[401]", e);
            requestLogin();
          }
        } catch (error) {
          console.error("[API CALL] Error while connecting server", error);
        }
      });
  }

  /**
   * Posts Request
   * @param {url} url url for posting request
   * @param {data} data data object to post to server
   */
  static postCall(url, data) {
    return getToken()
      .then((token) => {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        return new Promise(function (resolve, reject) {
          instanceSecure
            .post(url, data, config)
            .then(function (response) {
              resolve(response.data);
            })
            .catch(function (error) {
              reject(error);
            });
        });
      })
      .catch((e) => {
        try {
          console.error("[API CALL] Error:[POST]", e);
          if (e?.response?.status === 401) {
            console.error("[API CALL] Error while connecting server:[401]", e);
            requestLogin();
          }
          if (e?.response?.status === 429) {
            console.error("[API CALL] Error while connecting server:[429]", e);
            return e
          }

        } 
        catch (error) {
          console.error("[API CALL] Error while connecting server", error);
        }
      });
  }
  static postCallNew(url, data) {
    return new Promise(function (resolve, reject) {
      getToken()
        .then((token) => {
          const config = {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
          instanceSecure
            .post(url, data, config)
            .then(function (response) {
              resolve(response?.data);
            })
            .catch(function (error) {
              try {
                if (error?.response?.status === 401) {
                  requestLogin();
                } else {
                  reject(error.response);
                }
              } catch (error) {
                reject(error);
              }
            });
        })
        .catch((e) => {
          reject(e);
        });
    });
  }
  static getCallNew(url) {
    return new Promise(function (resolve, reject) {
      getToken()
        .then((token) => {
          const config = {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
          instanceSecure
            .get(url, config)
            .then(function (response) {
              resolve(response.data);
            })
            .catch(function (error) {
              try {
                if (error?.response?.status === 401) {
                  requestLogin();
                } else {
                  reject(error.response);
                }
              } catch (error) {
                reject(error);
              }
            });
        })
        .catch((e) => {
          reject(e);
        });
    });
  }
  static putCall(url, data) {
    return getToken()
      .then((token) => {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        return new Promise(function (resolve, reject) {
          instanceSecure
            .put(url, data, config)
            .then(function (response) {
              resolve(response.data);
            })
            .catch(function (error) {
              reject(error);
            });
        });
      })
      .catch((e) => {
        try {
          if (e?.response?.status === 401) {
            requestLogin();
          }
        } catch (error) {
          console.error("[API CALL] Error while connecting server", error);
        }
      });
  }

  static postCallWithMultipart(url, data) {
    return getToken()
      .then((token) => {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        return new Promise(function (resolve, reject) {
          instanceSecureForMultipart
            .post(url, data, config)
            .then(function (response) {
              resolve(response.data);
            })
            .catch(function (error) {
              reject(error);
            });
        });
      })
      .catch((e) => {
        try {
          if (e?.response?.status === 401) {
            requestLogin();
          }
        } catch (error) {
          console.error("[API CALL] Error while connecting server", error);
        }
      });
  }

  /**
   * delete Request
   * @param {url} url url for request
   */
  static deleteCall(url) {
    return getToken()
      .then((token) => {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
        return new Promise(function (resolve, reject) {
          instanceSecure
            .delete(url, config)
            .then(function (response) {
              resolve(response);
            })
            .catch(function (error) {
              reject(error);
            });
        });
      })
      .catch((e) => {
        try {
          console.error("[API CALL] Error:[GET]", e);
          if (e?.response?.status === 401) {
            console.error("[API CALL] Error while connecting server:[401]", e);
            requestLogin();
          }
        } catch (error) {
          console.error("[API CALL] Error while connecting server", error);
        }
      });
  }

  /**
   * Posts Request
   * @param {url} url url for posting request
   * @param {data} data data object to post to server
   */
  static postCallWithFileSave(url, data, fileName, fileExtension) {
    return getToken()
      .then((token) => {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        };
        return new Promise(function (resolve, reject) {
          instanceSecure
            .post(url, data, config)
            .then(function (response) {
              resolve(saveAs(response.data, fileName + "." + fileExtension));
            })
            .catch(function (error) {
              reject(error);
            });
        });
      })
      .catch((e) => {
        try {
          console.error("[API CALL] Error:[GET]", e);
          if (e?.response?.status === 401) {
            console.error("[API CALL] Error while connecting server:[401]", e);
            requestLogin();
          }
        } catch (error) {
          console.error("[API CALL] Error while connecting server", error);
        }
      });
  }
}
